import React from 'react'
import { useParams } from 'react-router-dom'
import { gql, useQuery } from '@apollo/client'
import { getGroupUrl } from 'helpers'

import { FormField } from 'js/components/Form/CustomFields/types'
import { Col, Row } from 'js/components/Grid/Grid'
import ItemLayout from 'js/components/Item/ItemLayout'
import Text from 'js/components/Text/Text'
import TiptapView from 'js/components/Tiptap/TiptapView'
import NotFound from 'js/core/NotFound'
import { genericArticleViewFragment } from 'js/genericArticle/fragments/genericArticleEntity'
import { showDateTime, showFullDate } from 'js/lib/helpers/date/showDate'

const Detail = () => {
    const params = useParams()
    const { guid } = params

    const { loading, data } = useQuery(GET_GENERIC_ARTICLE, {
        variables: {
            guid,
        },
    })

    const { data: viewerData, loading: viewerLoading } = useQuery(
        GET_VIEWER_RIGHTS,
        {
            variables: {
                subtype: data?.entity?.contentType?.key || '',
                groupGuid: data?.entity?.group?.guid || '',
            },
            skip: !data?.entity,
        },
    )

    if (loading || viewerLoading) return null
    if (!data || !data.entity || !viewerData) return <NotFound />

    const { entity } = data
    const { contentType, formResponse } = entity
    const { slug, formFields, hasFeaturedImage } = contentType

    const { detailViewTemplateIds } = contentType

    const onEdit = `${getGroupUrl(params)}/${slug}/edit/${guid}`
    const onAfterDelete = `${getGroupUrl(params)}/`

    return (
        <ItemLayout
            viewer={viewerData?.viewer}
            entity={{
                ...entity,
                // If showFeatured is disabled in admin, we should remove the possible featured image from the entity
                featured: hasFeaturedImage ? entity.featured : {},
            }}
            onEdit={onEdit}
            onAfterDelete={onAfterDelete}
            options={{
                author: detailViewTemplateIds.includes('author'),
                publishDate: detailViewTemplateIds.includes('publishDate'),
                views: detailViewTemplateIds.includes('views'),
                save: detailViewTemplateIds.includes('save'),
                likes: detailViewTemplateIds.includes('likes'),
                follow: detailViewTemplateIds.includes('follow'),
                share: detailViewTemplateIds.includes('share'),
                comments: detailViewTemplateIds.includes('comments'),
            }}
            {...({} as any)}
        >
            {formFields?.length > 0 && (
                <Row $spacing={16} style={{ marginBottom: '8px' }}>
                    {formFields.map(({ guid, title, fieldType }: FormField) => {
                        const value = formResponse.find(
                            (r) => r.guid === guid,
                        )?.value

                        if (!value) return null

                        return (
                            <Col mobileLandscapeUp={1 / 2} key={guid}>
                                <Text size="small" variant="grey">
                                    {title}
                                </Text>
                                <Text size="small">
                                    {fieldType === 'dateField' ? (
                                        <p>{showFullDate(value)}</p>
                                    ) : fieldType === 'dateTimeField' ? (
                                        <p>{showDateTime(value)}</p>
                                    ) : [
                                          'multiSelectField',
                                          'checkboxField',
                                      ].includes(fieldType) ? (
                                        <p>
                                            {value.split(',').map((v, i) => (
                                                <span key={v}>
                                                    {i > 0 && ', '}
                                                    {v}
                                                </span>
                                            ))}
                                        </p>
                                    ) : fieldType === 'htmlField' ? (
                                        <TiptapView
                                            content={value}
                                            textSize="small"
                                        />
                                    ) : (
                                        <p>{value}</p>
                                    )}
                                </Text>
                            </Col>
                        )
                    })}
                </Row>
            )}
        </ItemLayout>
    )
}

const GET_GENERIC_ARTICLE = gql`
    query GetGenericArticle($guid: String!) {
        entity(guid: $guid, incrementViewCount: true) {
            ${genericArticleViewFragment}
        }
    }
`

const GET_VIEWER_RIGHTS = gql`
    query GetViewerRights($subtype: String!, $groupGuid: String!) {
        viewer {
            guid
            loggedIn
            user {
                guid
                name
                icon
            }
            canInsertMedia(subtype: $subtype)
            requiresCommentModeration(subtype: $subtype, groupGuid: $groupGuid)
        }
    }
`

export default Detail
